import { useTrader } from '@klear/ui/hooks/use-trader'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '@klear/ui/components/breadcrumb'
import React from 'react'
import { useLocation, Link, useParams } from 'react-router'
import * as changeCase from 'change-case'
import {
  ROUTE_SUPPLIERS,
  ROUTE_ORDERS,
  buildNavPathTrader,
  ROUTE_CUSTOMERS,
  ROUTE_PAYABLE,
  ROUTE_PAYABLES
} from '@klear/constants'
import { pathWithoutTraderId } from '@/lib/nav'

type Crumb = { key: string; label: string; path?: string }

function payableCrumbs(traderId: string, segments: string[]): Crumb[] | null {
  if (segments[0] !== ROUTE_PAYABLE) return null
  return [{ key: 'payables', label: 'Payables', path: buildNavPathTrader(traderId, ROUTE_PAYABLES) }]
}

export function BreadcrumbNav() {
  const { traderId } = useParams()
  const { trader } = useTrader(traderId)
  const { pathname, state } = useLocation()

  if (!trader?.id) {
    return null
  }

  const segments = pathWithoutTraderId(pathname)

  const payable = payableCrumbs(trader.id.toString(), segments)
  if (payable) {
    return (
      <Breadcrumb>
        <BreadcrumbList>
          {payable.map((crumb, index) => (
            <React.Fragment key={crumb.key}>
              {index > 0 && <BreadcrumbSeparator />}
              <BreadcrumbItem>
                {crumb.path && index < payable.length - 1 ? (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.path}>{crumb.label}</Link>
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                )}
              </BreadcrumbItem>
            </React.Fragment>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
    )
  }

  const getDisplayName = (segment: string, index: number, fullPath: string[]) => {
    if (fullPath[index - 1] === ROUTE_ORDERS) {
      return state?.orderDetailTitle || segment
    }
    if ([ROUTE_CUSTOMERS, ROUTE_SUPPLIERS].includes(fullPath[index - 1] ?? '') && /^[0-9a-fA-F-]+$/.test(segment)) {
      return state?.organizationName || segment
    }

    return changeCase.capitalCase(segment)
  }

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {segments.map((segment, index) => {
          const path = buildNavPathTrader(trader.id.toString(), ...segments.slice(0, index + 1))
          const label = getDisplayName(segment, index, segments)

          return (
            <React.Fragment key={path}>
              {index > 0 && <BreadcrumbSeparator />}
              <BreadcrumbItem>
                {index === segments.length - 1 ? (
                  <BreadcrumbPage>{label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={path}>{label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </React.Fragment>
          )
        })}
      </BreadcrumbList>
    </Breadcrumb>
  )
}
